import { map, throttle } from "lodash";
import { fitContainer } from "./layout";

declare const jQuery: any;
const $ = jQuery;

export function timedCalls(func: Function, numCalls: number, wait: number): void
{
	for (let counter = 0; counter < numCalls; ++counter)
	{
		setTimeout(func, wait * counter);
	}
}

export function logObj(...xs): void
{
	console.log.apply(console, map(xs, x => {
		if (typeof x === 'object') return JSON.parse(JSON.stringify(x));
		return x;
	}))
}

export function safeParseJSON(json: string): any
{
	try
	{
		return JSON.parse(json);
	}
	catch (e)
	{
		console.error("Failed to parse JSON string", json, e);
		return undefined;
	}
}

export function ajaxCall(action: string, data: any = null): Promise<any>
{
	return new Promise<any>((resolve, reject) => {
		$.ajax({
			url: '/wp-admin/admin-ajax.php',
			type: 'POST',
			dataType: 'json',
			data: {
				action,
				data: JSON.stringify(data),
			},
			error: function error(x,y,z) {
				console.error("ajaxCall error",x,y,z);
				reject({
					response: x.responseJSON,
					status: x.status,
				});
			},
			success: resolve,
		});
	});
}

export function inArray(array: any[], value: any): boolean
{
	return array.indexOf(value) >= 0;
}

// array is mutated
export function uniquePush(array: any[], value: any): void
{
	if (!inArray(array, value))
	{
		array.push(value);
	}
}

export function isNully(value: any): boolean
{
    return value === undefined || value === null || (typeof value === "number" && isNaN(value));
}

export function isNullyOrEmptyString(value: any): boolean
{
    return value === "" || isNully(value);
}

// locale - string - e.g. "en-GB"
// currency - string - e.g. "GBP"
// includeSymbol - bool - whether to include currency symbol
export function formatMoney(num: number, locale: string, currency: string, includeSymbol: boolean = true): string|undefined
{
    if (isNullyOrEmptyString(num))
    {
        return undefined;
    }
    const formatted: string = new Intl.NumberFormat(locale, {
        style: "currency",
        currency
    }).format(num);
    return includeSymbol ? formatted : formatted.replace(/[^\d\.\-\,]/gi, "");
}

export function formatMoneyGB(num: number, includeSymbol: boolean = true): string|undefined
{
	return formatMoney(num, "en-GB", "GBP", includeSymbol);
}

export function extractBackgroundImageUrl($el): string|undefined
{
	const cssValue: string = $el.css("background-image");
	if (!cssValue)
	{
		return undefined;
	}
	return cssValue.replace(/(^url\s*\(\s*(\"|\')\s*)|(\s*(\"|\')\s*\)\s*$)/gi, "");
}

export function loadBackgroundImage($el): Promise<HTMLImageElement>
{
	return new Promise<HTMLImageElement>((resolve, reject) => {
		const url: string = extractBackgroundImageUrl($el);
		if (!url)
		{
			reject(new Error(`Element has no background image`));
			return;
		}
		resolve(loadImageUrl(url));
	});
}

export function loadImageUrls(urls: string[]): Promise<HTMLImageElement[]>
{
	return Promise.all(map(urls, loadImageUrl));
}

export function loadImageUrl(url: string): Promise<HTMLImageElement>
{
	return new Promise<HTMLImageElement>((resolve, reject) => {
		const image: HTMLImageElement = new Image();
		image.onload = () => {
			resolve(image);
		};
		image.onerror = (error) => {
			console.error("loadImageUrl error", error);
			reject(error);
		};
		image.src = url;
	});
}

export function getImageUrlDimensions(url: string): Promise<{ w: number, h: number }>
{
	return new Promise<{ w: number, h: number }>((resolve, reject) => {
		const image = new Image();
		image.onload = () => {
			resolve({
				w: image.width,
				h: image.height
			});
		};
		image.onerror = (err) => {
			reject(err);
			console.error("getImageUrlDimensions error", err);
		};
		image.src = url;
	});
}

export function debugShowViewportDimensions(): void
{
	if (!window["iw"] || !window["iw"].debugShowVPDims)
	{
		return;
	}
	const $div = $(`<div>`).appendTo(document.body).css({
		zIndex: 99999999,
		background: "#fff",
		padding: "3px",
		position: "fixed",
		right: "6px",
		bottom: "6px",
		color: "#000",
		fontSize: "12px",
		fontFamily: "sans-serif",
		lineHeight: "1em",
		border: "1px solid #bbb",
		cursor: "pointer"
	}).on("click", () => {
		$div.remove();
	});
	const $width = $(`<span>`).appendTo($div);
	$div.append(`<span> &times; </span>`);
	const $height = $(`<span>`).appendTo($div);
	const update = () => {
		$width.html(window.innerWidth.toString());
		$height.html(window.innerHeight.toString());
	};
	update();
	$(window).on("load resize orientationchange scroll", throttle(update, 99, { trailing: true }));
}

// Usage: await someTime(milliseconds)
export function someTime(milliseconds: number): Promise<void>
{
	return new Promise<void>((resolve, reject) => {
		setTimeout(() => {
			resolve();
		}, milliseconds);
	});
}

export function fileToDataUri(file: File): Promise<string|ArrayBuffer>
{
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = error => reject(error);
		reader.readAsDataURL(file);
	});
}

// Resolves with data URI
export async function getResizedImageSrcAsDataUri(src: string, width: number, height: number): Promise<string>
{
	const image: HTMLImageElement = await loadImageUrl(src);
	return getResizedImageAsDataUri(image, width, height);
}

// Resolves with data URI
// FIXME - add optional 'crop' param
export async function getResizedImageAsDataUri(image: HTMLImageElement, width: number, height: number): Promise<string>
{
	const origWidth: number = image.width;
	const origHeight: number = image.height;
	const dimensions: { w: number, h: number } = fitContainer(origWidth, origHeight, width, height);
	const canvas: HTMLCanvasElement = document.createElement("canvas");
	const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
	canvas.width = dimensions.w;
	canvas.height = dimensions.h;
	ctx.drawImage(image, 0, 0, dimensions.w, dimensions.h);
	return canvas.toDataURL();
}

export function zeroPadNumber(num: number): string
{
	if (num < 10)
	{
		return "0" + num;
	}
	return num.toString();
}

if (!window["iw"])
{
	window["iw"] = {};
}
if (!window["iw"].utils)
{
	window["iw"].utils = {};
}

(function () {
	const utils = window["iw"].utils;
	utils.logObj = logObj;
	utils.safeParseJSON = safeParseJSON;
	utils.ajaxCall = ajaxCall;
	utils.inArray = inArray;
	utils.uniquePush = uniquePush;
	utils.isNully = isNully;
	utils.isNullyOrEmptyString = isNullyOrEmptyString;
	utils.formatMoney = formatMoney;
	utils.formatMoneyGB = formatMoneyGB;
	utils.extractBackgroundImageUrl = extractBackgroundImageUrl;
	utils.loadBackgroundImage = loadBackgroundImage;
	utils.loadImageUrls = loadImageUrls;
	utils.loadImageUrl = loadImageUrl;
	utils.getImageUrlDimensions = getImageUrlDimensions;
	utils.someTime = someTime;
	utils.fileToDataUri = fileToDataUri;
	utils.getResizedImageSrcAsDataUri = getResizedImageSrcAsDataUri;
	utils.getResizedImageAsDataUri = getResizedImageAsDataUri;
	utils.zeroPadNumber = zeroPadNumber;
})();
